import { functions, auth, getFunctionsUrl } from './firebaseClient';
import { httpsCallable } from 'firebase/functions';

// Functions deployed as onCall (httpsCallable) instead of onRequest
const CALLABLE_FUNCTIONS = new Set(['sendEmailIMAP']);

/**
 * Call a Firebase Cloud Function with authentication.
 * Routes to httpsCallable for onCall functions, fetch+Bearer for onRequest.
 */
async function callFunction(functionName, data) {
  if (CALLABLE_FUNCTIONS.has(functionName)) {
    const result = await httpsCallable(functions, functionName)(data);
    return result.data;
  }

  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const idToken = await user.getIdToken();
  const baseUrl = getFunctionsUrl();

  const response = await fetch(`${baseUrl}/${functionName}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Function ${functionName} failed`);
  }

  return response.json();
}

/**
 * Core integrations - replacing Base44 InvokeLLM, SendEmail, etc.
 */
export const Core = {
  /**
   * Invoke LLM (now using Gemini via Cloud Functions)
   * Maintains Base44 API compatibility
   */
  async InvokeLLM({ prompt, response_json_schema, model }) {
    return callFunction('invokeLLM', {
      prompt,
      response_json_schema,
      model: model || 'gemini-1.5-flash',
    });
  },

  /**
   * Send email via SMTP
   */
  async SendEmail({ to, subject, body, attachments = [], lead_id, in_reply_to, references }) {
    return callFunction('sendEmailIMAP', {
      to_email: to,
      subject,
      body,
      attachments,
      lead_id,
      in_reply_to,
      references,
    });
  },

  /**
   * Upload file to Firebase Storage
   */
  async UploadFile(file) {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
    const storage = getStorage();
    
    const fileName = `${Date.now()}_${file.name}`;
    const storageRef = ref(storage, `uploads/${user.uid}/${fileName}`);
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    return { 
      url: downloadURL, 
      filename: file.name,
      path: `uploads/${user.uid}/${fileName}`
    };
  },

  /**
   * Generate image (placeholder - implement with Gemini if needed)
   */
  async GenerateImage({ prompt }) {
    return callFunction('generateImage', { prompt });
  },

  /**
   * Extract data from uploaded file
   */
  async ExtractDataFromUploadedFile({ file_url }) {
    return callFunction('extractDataFromFile', { file_url });
  },
};

// Export individual functions for backward compatibility
export const InvokeLLM = Core.InvokeLLM;
export const SendEmail = Core.SendEmail;
export const SendSMS = async (data) => {
  console.warn('SendSMS not implemented in Firebase version');
  return { success: false, message: 'Not implemented' };
};
export const UploadFile = Core.UploadFile;
export const GenerateImage = Core.GenerateImage;
export const ExtractDataFromUploadedFile = Core.ExtractDataFromUploadedFile;

/**
 * Backend Functions - Direct calls to Cloud Functions
 */
export const BackendFunctions = {
  // Email functions
  sendEmail: (data) => callFunction('sendEmailIMAP', data),
  fetchEmails: () => callFunction('fetchEmails', {}),
  procesarEmailsEntrantes: () => callFunction('procesarEmailsEntrantes', {}),
  
  // Seguimientos
  procesarSeguimientos: () => callFunction('procesarSeguimientos', {}),
  
  // Webhooks
  sendDiscordAlert: (data) => callFunction('sendDiscordAlert', data),
  
  // Importación
  importarLeadsCSV: (data) => callFunction('importarLeadsCSV', data),

  // Factura Directa
  createFacturaDirectaContact: (data) => callFunction('createFacturaDirectaContact', data),
  createFacturaDirectaInvoice: (data) => callFunction('createFacturaDirectaInvoice', data),

  // #17 Sugerencia respuesta IA
  sugerirRespuestaEmail: (data) => callFunction('sugerirRespuestaEmail', data),
};

/**
 * Invoke a Cloud Function by name
 * This matches the base44.functions.invoke API
 * @param {string} functionName - Name of the function to call
 * @param {Object} data - Data to send to the function
 * @returns {Promise<Object>} Response with data property
 */
export async function invokeFunctions(functionName, data = {}) {
  const result = await callFunction(functionName, data);
  return { data: result };
}
